import { useEffect, useState } from "react";
import { investors as api, type Investor, type InvestorType } from "../../api";
import { Link, useRouter } from "../../router";
import { ClientTypeIcon } from "../../components/ClientTypeIcon";

type TypeFilter = "all" | InvestorType;

const FILTERS: { key: TypeFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "person", label: "Persons" },
  { key: "company", label: "Companies" },
  { key: "group", label: "Groups" },
];

export function InvestorList() {
  const { navigate } = useRouter();
  const [items, setItems] = useState<Investor[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<TypeFilter>("all");

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(() => {
      api
        .list(search, filter === "all" ? undefined : filter)
        .then((d) => {
          if (!cancelled) setItems(d.investors);
        })
        .catch((e) => {
          if (!cancelled) setError(e.message);
        });
    }, 120);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [search, filter]);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <span className="eyebrow">CRM</span>
          <h1>Investors</h1>
        </div>
        <Link to="/app/crm/new" className="btn btn-primary">
          + New investor
        </Link>
      </header>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search by name, email, phone, or address"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search"
        />
      </div>

      <nav className="tabs">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={filter === f.key ? "tab-link active" : "tab-link"}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </nav>

      {error && <div className="banner-error">{error}</div>}

      {items === null ? (
        <div className="muted">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty">
          <p>No investors {search ? "match your search" : "yet"}.</p>
          {!search && (
            <Link to="/app/crm/new" className="btn btn-ghost">
              Add the first investor
            </Link>
          )}
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>City</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr
                key={i.id}
                onClick={() => navigate(`/app/crm/${i.id}`)}
                className="row-link"
              >
                <td className="strong">
                  <span className="cell-with-icon">
                    <ClientTypeIcon type={i.type} className="type-icon" />
                    {i.name}
                  </span>
                </td>
                <td>{i.email ?? "—"}</td>
                <td>{i.phone ?? "—"}</td>
                <td>{i.city ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
